import React, { useMemo, useState } from 'react';

import api from '../api';
import { evidencePairsToObject, GOALS_TEXT_SCOPE_STYLES, normalizeEvidencePairs } from './shared';

const emptyEvidenceRow = () => ({ key: '', value: '' });

export default function ProgressUpdateForm({ goal, onSaved }) {
  const initialEvidence = useMemo(() => {
    const latest = (goal?.progress_updates || [])[0];
    const pairs = normalizeEvidencePairs(latest?.supporting_data);
    return pairs.length ? pairs : [emptyEvidenceRow()];
  }, [goal]);

  const [form, setForm] = useState({
    progress_percentage: goal?.completion_percentage || 0,
    current_value: goal?.current_value || 0,
    milestone_reached: '',
    update_notes: '',
  });
  const [evidenceRows, setEvidenceRows] = useState(initialEvidence);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateEvidenceRow = (index, field, value) => {
    setEvidenceRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row)));
  };

  const addEvidenceRow = () => setEvidenceRows((prev) => [...prev, emptyEvidenceRow()]);
  const removeEvidenceRow = (index) => {
    setEvidenceRows((prev) => (prev.length === 1 ? [emptyEvidenceRow()] : prev.filter((_, idx) => idx !== index)));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post(`/goals/${goal.id}/progress`, {
        ...form,
        supporting_data: evidencePairsToObject(evidenceRows),
      });
      setForm((prev) => ({ ...prev, milestone_reached: '', update_notes: '' }));
      setEvidenceRows([emptyEvidenceRow()]);
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save progress update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="goals-text-scope" onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
          Completion Progress
          <div style={{ border: '1px solid #cbd5e1', borderRadius: 14, padding: 12, background: '#f8fafc' }}>
            <input
              type="range"
              min="0"
              max="100"
              value={form.progress_percentage}
              onChange={(e) => setForm({ ...form, progress_percentage: Number(e.target.value) })}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 8 }}>
              <span>0%</span>
              <strong style={{ fontSize: 18 }}>{form.progress_percentage}%</strong>
              <span>100%</span>
            </div>
          </div>
        </label>

        <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
          Current Achieved Value
          <input
            value={form.current_value}
            onChange={(e) => setForm({ ...form, current_value: e.target.value })}
            style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1', background: '#fff' }}
          />
          <span style={{ fontSize: 12 }}>Target unit: {goal.target_unit}</span>
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
          Milestone Reached
          <input
            value={form.milestone_reached}
            onChange={(e) => setForm({ ...form, milestone_reached: e.target.value })}
            placeholder="Example: Monthly target completed"
            style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1', background: '#fff' }}
          />
        </label>
        <label style={{ display: 'grid', gap: 8, fontWeight: 700 }}>
          Update Notes
          <textarea
            rows="3"
            value={form.update_notes}
            onChange={(e) => setForm({ ...form, update_notes: e.target.value })}
            placeholder="Explain what was completed, blockers, or why progress changed"
            style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1', background: '#fff' }}
          />
        </label>
      </div>

      <div style={{ border: '1px solid #dbeafe', borderRadius: 18, background: '#f8fbff', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Supporting Evidence</div>
            <div style={{ fontSize: 13 }}>Add simple label/value pairs. No JSON needed.</div>
          </div>
          <button type="button" onClick={addEvidenceRow} style={{ border: 0, borderRadius: 10, background: '#1d4ed8', color: '#fff', padding: '10px 12px', fontWeight: 700, cursor: 'pointer' }}>
            Add Evidence Row
          </button>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {evidenceRows.map((row, index) => (
            <div key={`${index}-${row.key}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10 }}>
              <input
                value={row.key}
                onChange={(e) => updateEvidenceRow(index, 'key', e.target.value)}
                placeholder="Label, e.g. Sales MTD"
                style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff' }}
              />
              <input
                value={row.value}
                onChange={(e) => updateEvidenceRow(index, 'value', e.target.value)}
                placeholder="Value, e.g. 6500000 or 45 clients"
                style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff' }}
              />
              <button type="button" onClick={() => removeEvidenceRow(index)} style={{ border: 0, borderRadius: 12, background: '#fee2e2', color: '#b91c1c', padding: '0 14px', fontWeight: 800, cursor: 'pointer' }}>
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {error ? <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{error}</div> : null}

      <button type="submit" disabled={saving} style={{ border: 0, borderRadius: 14, background: '#0f766e', color: '#fff', padding: '14px 16px', fontWeight: 800, cursor: 'pointer' }}>
        {saving ? 'Saving update...' : 'Save Progress Update'}
      </button>
    </form>
  );
}
